import { resolveApiUrl } from '@/lib/api'
import { getActiveArtistId, getAuthToken } from '@/api/client'

const FRONTEND_LOG_ENDPOINT = resolveApiUrl('/observability/frontend')
const DEDUP_INTERVAL_MS = 30_000
const RATE_LIMIT_INTERVAL_MS = 60_000
const RATE_LIMIT_MAX_EVENTS = 40
const RATE_LIMIT_COOLDOWN_MS = 120_000
const NETWORK_ERROR_DEDUP_INTERVAL_MS = 1_000
const APP_VERSION =
  (typeof globalThis !== 'undefined' && (globalThis as { __APP_VERSION__?: string }).__APP_VERSION__) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_BUILD_VERSION) ||
  'dev'

export type FrontendErrorSeverity =
  | 'error'
  | 'warn'
  | 'network'
  | 'runtime'
  | 'react'
  | 'swr'
  | 'console'

export interface FrontendErrorPayload {
  message: string
  severity: FrontendErrorSeverity
  route?: string
  stack?: string
  componentName?: string
  userId?: string
  version?: string
  metadata?: Record<string, any>
  timestamp?: string
}

export type LoggableFrontendError = Partial<FrontendErrorPayload> &
  Pick<FrontendErrorPayload, 'message' | 'severity'>

const lastSentAt = new Map<string, number>()
const recentEventTimestamps: number[] = []
let rateLimitHoldUntil = 0
let lastNetworkErrorKey: string | null = null
let lastNetworkErrorTimestamp = 0

type LogDelegate = (payload: LoggableFrontendError) => Promise<void>
type FlaggedWindow = Window &
  Partial<{
    __frontendErrorHooksInstalled: boolean
    __networkLoggingInstalled: boolean
  }>

const getFlaggedWindow = (): FlaggedWindow | undefined =>
  typeof window === 'undefined' ? undefined : (window as FlaggedWindow)

const getWindowOrigin = (): string | undefined =>
  typeof window === 'undefined' ? undefined : window.location?.origin

const toAbsoluteUrl = (candidate?: string): string | undefined => {
  if (!candidate) {
    return undefined
  }
  try {
    const origin = getWindowOrigin() ?? 'http://localhost'
    return new URL(candidate, origin).href
  } catch {
    return undefined
  }
}

const isObservabilityRequest = (url?: string): boolean => {
  const requestUrl = toAbsoluteUrl(url)
  if (!requestUrl) {
    return false
  }
  const observabilityUrl = toAbsoluteUrl(FRONTEND_LOG_ENDPOINT)
  return observabilityUrl !== undefined && requestUrl === observabilityUrl
}

const shouldLogNetworkStatus = (status: number): boolean => status >= 500

const shouldBypassNetworkLogging = (url?: string): boolean => isObservabilityRequest(url)

const requestUrlFromInput = (input: Parameters<typeof fetch>[0]): string | undefined => {
  if (typeof input === 'string') {
    return input
  }
  if (typeof URL !== 'undefined' && input instanceof URL) {
    return input.href
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.url
  }
  if (typeof input === 'object' && input && 'url' in (input as Record<string, unknown>)) {
    const maybeUrl = (input as { url?: string }).url
    if (typeof maybeUrl === 'string') {
      return maybeUrl
    }
  }
  const fallback = (input as { toString?: () => string })?.toString?.()
  return typeof fallback === 'string' ? fallback : undefined
}

const isProductionEnv = (): boolean => {
  const maybeProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } })
    .process
  if (maybeProcess?.env?.NODE_ENV) {
    return maybeProcess.env.NODE_ENV === 'production'
  }

  if (typeof import.meta !== 'undefined' && import.meta?.env?.PROD !== undefined) {
    return Boolean(import.meta.env.PROD)
  }

  return false
}

const shouldSendError = (payload: FrontendErrorPayload): boolean => {
  const key = [
    payload.severity,
    payload.route ?? '',
    payload.message,
    payload.stack ?? '',
  ].join('::')
  const now = Date.now()
  const lastSeen = lastSentAt.get(key) ?? 0

  if (now - lastSeen < DEDUP_INTERVAL_MS) {
    return false
  }

  lastSentAt.set(key, now)
  return true
}

const withinRateBudget = (): boolean => {
  const now = Date.now()
  if (now < rateLimitHoldUntil) {
    return false
  }
  while (recentEventTimestamps.length > 0 && now - recentEventTimestamps[0] > RATE_LIMIT_INTERVAL_MS) {
    recentEventTimestamps.shift()
  }
  if (recentEventTimestamps.length >= RATE_LIMIT_MAX_EVENTS) {
    rateLimitHoldUntil = now + RATE_LIMIT_COOLDOWN_MS
    return false
  }
  recentEventTimestamps.push(now)
  return true
}

const shouldEmitFrontendError = (payload: LoggableFrontendError): boolean => {
  if (payload.severity !== 'network') {
    return true
  }
  const metadataUrl = typeof payload.metadata?.url === 'string' ? payload.metadata.url : undefined
  const key = `network:${metadataUrl ?? 'unknown'}`
  const now = Date.now()
  if (key === lastNetworkErrorKey && now - lastNetworkErrorTimestamp < NETWORK_ERROR_DEDUP_INTERVAL_MS) {
    return false
  }
  lastNetworkErrorKey = key
  lastNetworkErrorTimestamp = now
  return true
}

const logFrontendErrorImpl: LogDelegate = async (
  payload: LoggableFrontendError,
): Promise<void> => {
  const route =
    payload.route ??
    (typeof window !== 'undefined' ? window.location?.pathname ?? undefined : undefined)
  const normalized: FrontendErrorPayload = {
    ...payload,
    route,
    version: payload.version ?? APP_VERSION,
    timestamp: new Date().toISOString(),
  }

  if (!isProductionEnv()) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('[frontend-error]', normalized)
    }
    return
  }

  if (!shouldSendError(normalized)) {
    return
  }

  if (!withinRateBudget()) {
    return
  }

  if (typeof fetch !== 'function') {
    return
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const token = getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    const artistId = getActiveArtistId()
    if (artistId) {
      headers['X-Artist-Id'] = artistId
    }

    const response = await fetch(FRONTEND_LOG_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(normalized),
      keepalive: true,
    })

    if (response.status === 429) {
      rateLimitHoldUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS
    }
  } catch (err) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('Failed to send frontend error log', err)
    }
  }
}

let logFrontendErrorDelegate: LogDelegate = logFrontendErrorImpl

const emitFrontendError = (payload: LoggableFrontendError): Promise<void> => {
  if (!shouldEmitFrontendError(payload)) {
    return Promise.resolve()
  }
  return logFrontendErrorDelegate(payload)
}

export const logFrontendError = (payload: LoggableFrontendError): Promise<void> =>
  emitFrontendError(payload)

export const __setFrontendErrorLogger = (delegate?: LogDelegate): void => {
  logFrontendErrorDelegate = delegate ?? logFrontendErrorImpl
}

let hooksRegistered = false

const installNetworkLogging = (): void => {
  const flaggedWindow = getFlaggedWindow()
  if (!flaggedWindow || flaggedWindow.__networkLoggingInstalled) {
    return
  }
  flaggedWindow.__networkLoggingInstalled = true

  if (typeof flaggedWindow.fetch === 'function') {
    const originalFetch = flaggedWindow.fetch
    window.fetch = async (...args: Parameters<typeof originalFetch>) => {
      const [input, init] = args
      const url = requestUrlFromInput(input)
      const skipLogging = shouldBypassNetworkLogging(url)
      try {
        const response = await originalFetch(...args)
        if (!response.ok && !skipLogging && shouldLogNetworkStatus(response.status)) {
          void emitFrontendError({
            message: `Fetch responded with status ${response.status} for ${url ?? 'unknown'}`,
            severity: 'network',
            metadata: {
              url: url ?? 'unknown',
              status: response.status,
              statusText: response.statusText,
              method: init?.method ?? 'GET',
            },
          })
        }
        return response
      } catch (error) {
        if (!skipLogging) {
          void emitFrontendError({
            message: `Fetch threw for ${url ?? 'unknown'}`,
            severity: 'network',
            metadata: { url: url ?? 'unknown', error },
          })
        }
        throw error
      }
    }
  }

  const xhr = flaggedWindow.XMLHttpRequest
  if (typeof xhr === 'function') {
    const originalOpen = xhr.prototype.open
    const originalSend = xhr.prototype.send

    xhr.prototype.open = function (method: string, url: string, ...rest: any[]) {
      ;(this as XMLHttpRequest & {
        __logMeta?: { method: string; url: string; skipLogging: boolean }
      }).__logMeta = {
        method,
        url,
        skipLogging: shouldBypassNetworkLogging(url),
      }
      return originalOpen.call(this, method, url, ...rest)
    }

    xhr.prototype.send = function (...sendArgs: any[]) {
      const meta = (this as XMLHttpRequest & {
        __logMeta?: { method: string; url: string; skipLogging: boolean }
      }).__logMeta
      if (meta?.skipLogging) {
        return originalSend.apply(this, sendArgs)
      }

      const maybeLog = (eventType: string, status: number): void => {
        if (eventType !== 'error' && !shouldLogNetworkStatus(status)) {
          return
        }
        void emitFrontendError({
          message: `XHR ${eventType} for ${meta?.url ?? 'unknown'}`,
          severity: 'network',
          metadata: {
            url: meta?.url,
            method: meta?.method,
            status: this.status,
            readyState: this.readyState,
          },
        })
      }

      const logNetworkError = (eventType: string): void => {
        maybeLog(eventType, this.status)
      }

      this.addEventListener('error', () => logNetworkError('error'))
      this.addEventListener('load', () => {
        if (shouldLogNetworkStatus(this.status)) {
          logNetworkError('response')
        }
      })

      return originalSend.apply(this, sendArgs)
    }
  }
}

const registerGlobalHooks = (): void => {
  if (hooksRegistered) {
    return
  }
  const flaggedWindow = getFlaggedWindow()
  if (!flaggedWindow) {
    return
  }
  if (flaggedWindow.__frontendErrorHooksInstalled) {
    hooksRegistered = true
    return
  }
  flaggedWindow.__frontendErrorHooksInstalled = true
  hooksRegistered = true

  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    const originalConsoleError = console.error
    console.error = (...args: unknown[]) => {
      const message = args
        .map((arg) => (arg instanceof Error ? arg.message : String(arg)))
        .join(' ')
      void emitFrontendError({
        message,
        severity: 'console',
        metadata: { args },
      })
      originalConsoleError(...args)
    }
  }

  const priorWindowError = window.onerror
  window.onerror = (message, source, lineno, colno, error): boolean | void => {
    const stack = error instanceof Error ? error.stack : undefined
    void emitFrontendError({
      message: String(message),
      severity: 'runtime',
      stack,
      metadata: { source, lineno, colno },
    })

    if (typeof priorWindowError === 'function') {
      return priorWindowError(message, source, lineno, colno, error)
    }
    return false
  }

  const priorUnhandledRejection = window.onunhandledrejection
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const reason = event?.reason
    const message =
      reason instanceof Error ? reason.message : reason ? String(reason) : 'Unhandled rejection'
    const stack = reason instanceof Error ? reason.stack : undefined
    void emitFrontendError({
      message,
      severity: 'runtime',
      stack,
      metadata: { reason },
    })

    if (typeof priorUnhandledRejection === 'function') {
      priorUnhandledRejection(event)
    }
  }
}

registerGlobalHooks()
installNetworkLogging()
